import type { KeyboardEvent } from 'react';
import { useCallback, useRef, useState } from 'react';

export interface UseResizeProps {
  initial: number;
  max?: number;
  min?: number;
  persistAs?: string;
  /** Pixels one arrow key press moves the edge. */
  step?: number;
}

export const useResizeWidth = (
  { initial, max, min = 0, persistAs, step = 10 }: UseResizeProps = { initial: 0 },
) => {
  const resizableRef = useRef<HTMLElement>();
  const dragged = useRef(false);
  const persistedWidth = persistAs ? localStorage.getItem(persistAs) : null;
  const [width, setWidthState] = useState(persistedWidth ? Number(persistedWidth) : initial);

  const setWidth = useCallback(
    (newWidth: number) => {
      if (newWidth < min) return;
      if (max && newWidth > max) return;

      setWidthState(newWidth);

      if (persistAs) localStorage.setItem(persistAs, newWidth.toString());
    },
    [max, min, persistAs],
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!resizableRef.current) return;
      dragged.current = true;
      setWidth(event.clientX - resizableRef.current.offsetLeft);
    },
    [setWidth],
  );

  const handleMouseUp = useCallback(() => {
    document.body.style.userSelect = '';
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  const onResize = useCallback(() => {
    dragged.current = false;
    document.body.style.userSelect = 'none';
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove, handleMouseUp]);

  // Arrow keys for a role="separator" handle; Home and End jump to the bounds.
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const target = {
        ArrowLeft: width - step,
        ArrowRight: width + step,
        End: max,
        Home: min,
      }[event.key];
      if (target === undefined) return;
      event.preventDefault();
      setWidth(Math.min(max ?? target, Math.max(min, target)));
    },
    [max, min, setWidth, step, width],
  );

  // A click without a drag swaps between the bounds, so resizing needs no dragging (WCAG 2.5.7).
  const onToggle = useCallback(() => {
    if (dragged.current || max === undefined) return;
    setWidth(width < (min + max) / 2 ? max : min);
  }, [max, min, setWidth, width]);

  return { max, min, onKeyDown, onResize, onToggle, resizableRef, setWidth, width };
};
